import styled from '@emotion/styled';
import { Text, tokens } from '@hotjar/hotsauce';
import { useCallback, useMemo } from 'react';

import { XToolConnector } from '@hotjar/insights-common';
import { useTranslation } from '@hotjar/translations';
import { useSurveyResponseQuery } from '@hotjar/feedback/domain/Surveys/query';
import { useSite } from '@hotjar/site';
import { useSurveyQuestions } from '@hotjar/feedback/hooks/survey/useSurveyQuestions';
import { QuestionTypes, type SurveyQuestion } from '@hotjar/feedback/domain/Surveys/Questions';
import type { SurveyResponseDto } from '@hotjar/feedback/domain/Surveys/Responses/SurveyResponseDto';
import { colorOtherCategory, dataColorRange } from '@hotjar/charts';

const Title = styled(Text)`
  word-spacing: 10px;
`;

type NPSCategory = 'promoter' | 'passive' | 'detractor';

function getNPSCategory(score: number): NPSCategory {
  if (score >= 9) {
    return 'promoter';
  }

  if (score >= 7) {
    return 'passive';
  }

  return 'detractor';
}

const lineColors: Record<NPSCategory, string> = {
  detractor: tokens.color.ember_500,
  passive: colorOtherCategory.shade_300,
  promoter: dataColorRange[4].shade_400,
};

function getFirstNPSScore(
  response: SurveyResponseDto,
  questions: SurveyQuestion[],
): number | undefined {
  for (let i = 0; i < response.content.answers.length; i += 1) {
    const question = questions.find((q) =>
      q.uuid.startsWith(response.content.answers[i].questionUuid),
    );
    if (question !== undefined) {
      if (question.type === QuestionTypes.NET_PROMOTER_SCORE) {
        return Number(response.content.answers[i].answer);
      }
    }
  }
  return undefined;
}

function getFirstOpenEndedAnswerAfterNPSAnswer(
  response: SurveyResponseDto,
  questions: SurveyQuestion[],
): string | undefined {
  let foundNPSQuestion = false;
  for (let i = 0; i < response.content.answers.length; i += 1) {
    const question = questions.find((q) =>
      q.uuid.startsWith(response.content.answers[i].questionUuid),
    );
    if (question !== undefined) {
      if (question.type === QuestionTypes.NET_PROMOTER_SCORE) {
        foundNPSQuestion = true;
      } else if (
        (foundNPSQuestion && question.type === QuestionTypes.SINGLE_OPEN_ENDED_MULTI_LINE) ||
        QuestionTypes.SINGLE_OPEN_ENDED_SINGLE_LINE
      ) {
        return response.content.answers[i].answer.toString();
      }
    }
  }
  return undefined;
}

type NPSSurveyConnectorProps = {
  siteId: number;
  surveyId: number | string;
  responseId: number | string;
  targetUrl: string;
};

export const NPSSurveyConnector = ({
  siteId,
  surveyId,
  responseId,
  targetUrl,
}: NPSSurveyConnectorProps): JSX.Element => {
  const { t } = useTranslation('feedback', { keyPrefix: 'nps-survey-connector' });
  const { data: site } = useSite(siteId);
  const { questions, questionsStatus } = useSurveyQuestions(Number(surveyId));
  const {
    data: response,
    isLoading: isResponseLoading,
    isFetched: isResponseFetched,
  } = useSurveyResponseQuery(site?.id, Number(surveyId), Number(responseId), true);

  const openResponse = useCallback(() => {
    window.location.assign(targetUrl);
  }, [targetUrl]);

  const score = useMemo(
    () =>
      response !== undefined && questions !== undefined
        ? getFirstNPSScore(response, questions)
        : undefined,
    [response, questions],
  );

  const { title, lineColor } = useMemo(() => {
    if (score === undefined) return { title: undefined, lineColor: undefined };
    const category = getNPSCategory(score);
    return {
      lineColor: lineColors[category],
      title: (
        <Title textType="subtitle2">
          {score} {t(category)}
        </Title>
      ),
    };
  }, [score, t]);

  const helperText = useMemo(() => {
    if (response === undefined || questions === undefined) return undefined;
    return getFirstOpenEndedAnswerAfterNPSAnswer(response, questions);
  }, [response, questions]);

  const isLoading = isResponseLoading || !isResponseFetched || questionsStatus === 'loading';

  return (
    <XToolConnector
      title={title}
      description={t('description')}
      isLoading={isLoading}
      helperText={helperText}
      actionButtonIcon="surveys"
      actionButtonText={t('view-response')}
      actionButtonDisabled={response === undefined}
      errorMessage={!isLoading && response === undefined ? t('no-response-error') : undefined}
      onActionButtonClick={openResponse}
      lineColor={lineColor}
    />
  );
};
